import { createReducer } from 'redux-starter-kit';
import {
  addPublishInspectionReason, cancelEditInspectionReason, editInspectionReason,
  fetchAddInspectionReason, fetchInspectionReasonPageData, fetchUpdateInspectionReason,
  removePublishInspectionReason,
} from './inspectionReasonUiActions';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import { selectChecklistMap } from '../../../domain/checklists/checklistsReducer';
import { selectChecklistsConfigMap } from '../../../domain/checklistsConfig/checklistsConfigReducer';

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the inspection reason being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.inspectionReasons.editState;

/**
 * A selector for finding the current publishing inspection reasons.
 * @param {object} state The redux state of the application.
 * @returns An array of inspection reason codes.
 * @example const loadingAreaCodes = selectLoadingDamageAreaCodes(state);
 */
export const selectLoadingActiveStatus = state => state.via.ui.inspectionReasons.pendingPublishInspectionReasons;

/**
 * A selector that returns the state of the 'FETCH_INSPECTION_REASONS_PAGE_DATA' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const inspectionReasonRequest = selectRequestedInspectionReasons(state);
 */
export const selectInspectionReasonsPageDataRequest = (state) => {
  const requestId = state.via.ui.inspectionReasons.requestInspectionReasonsPageDataId;
  if (!requestId) return null;
  return selectRequest(state, requestId);
};

/**
 * A selector that returns the currently loaded checklists.
 * Null if not currently loaded, otherwise a list of the checklists.
 * @param {state} state The redux state.
 * @returns Null if not loaded, or a list of the inspection reasons.
 * @example const inspectionReasons = selectInspectionReasons(state);
 */
export const selectChecklists = (state) => {
  const checklistMap = selectChecklistMap(state);
  return checklistMap == null ? [] : Object.values(checklistMap);
};

/**
 * A selector that returns the currently loaded configuration for all checklists.
 * Null if not currently loaded, otherwise a list of the checklists.
 * @param {state} state The redux state.
 * @returns Null if not loaded, or a list of the checklists.
 */
export const selectChecklistsConfig = (state) => {
  const checklistMap = selectChecklistsConfigMap(state);
  return checklistMap == null ? [] : Object.values(checklistMap);
};

/**
 * A selector for the current 'FETCH_CREATE_INSPECTION_REASON' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addInspectionReasonsRequest = selectInspectionReasonRequest(state);
 */
export const selectSubmitInspectionReasonRequest = (state) => {
  const requestId = state.via.ui.inspectionReasons.editState.requestSubmitInspectionReasonId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

const initialEditingState = {
  isEditing: false,
  reasonCode: null,
  requestSubmitInspectionReasonId: null,
  validationMessages: ({
    title: '',
    messages: [],
    visible: false,
  }),
};

const inspectionReasonsInitialState = {
  editState: initialEditingState, // Is a inspection reason being edited/created.
  pendingPublishInspectionReasons: [], // Inspection reasons being submitted for publishing.
  requestInspectionReasonsPageDataId: null, // Request id for loading inspection reasons page data.
};

/**
 * A reducer for handling the UI state changes for the Inspection reason Configuration screen.
 */
const inspectionReasonReducer = createReducer(inspectionReasonsInitialState, {
  // The user has started editing the inspection reason.
  [editInspectionReason]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      reasonCode: action.payload,
    },
  }),

  // The user has stopped editing the inspection reason.
  [cancelEditInspectionReason]: state => ({
    ...state,
    editState: {
      ...initialEditingState,
    },
  }),

  // A new requst has been made to add a inspection reason.
  [fetchAddInspectionReason]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitInspectionReasonId: action.payload,
    },
  }),

  // A new request has been made to update a inspection reason.
  [fetchUpdateInspectionReason]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitInspectionReasonId: action.payload,
    },
  }),

  // A new request has been made to load all the inspection reasons page data
  [fetchInspectionReasonPageData]: (state, action) => ({
    ...state,
    requestInspectionReasonsPageDataId: action.payload,
  }),

  // A inspection reason is being published. So add it to state.
  [addPublishInspectionReason]: (state, action) => ({
    ...state,
    pendingPublishInspectionReasons: [...state.pendingPublishInspectionReasons, action.payload],
  }),

  // A inspection reason has finished publishing. Remove it from state.
  [removePublishInspectionReason]: (state, action) => ({
    ...state,
    pendingPublishInspectionReasons: state.pendingPublishInspectionReasons.filter(code => `${code}` !== `${action.payload}`),
  }),
});

export default inspectionReasonReducer;
